import { queryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { apiClient } from '@/services/api/client';
import { sendBackendEvent } from '@/services/monitoring/backendEvents';

import type { TourId } from '@/services/appEvents';

/** The user preference the backend keeps finished tours under. */
const PREFERENCE_KEY = 'product_tours';
const PREFERENCE_URL = `/api/preference/${PREFERENCE_KEY}/`;

/** Tours the user has been through (finished or skipped alike). */
export type TourRecords = Partial<Record<TourId, true>>;

/** A user who has never been through a tour has no preference yet (404). */
async function fetchTourRecords(): Promise<{ exists: boolean; tours: TourRecords }> {
  try {
    const { data } = await apiClient.get<{ data: TourRecords }>(PREFERENCE_URL);
    return { exists: true, tours: data };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return { exists: false, tours: {} };
    throw error;
  }
}

export const tourRecordsQuery = (userId: number | undefined) =>
  queryOptions({
    queryKey: ['preference', PREFERENCE_KEY, userId],
    queryFn: fetchTourRecords,
    staleTime: Infinity,
    retry: false,
  });

/**
 * Remember that the user has been through a tour. The preference holds every
 * tour, so this reads the latest and adds this one, creating it the first time.
 * Read-then-write can lose a concurrent write of a *different* tour from another
 * tab; that tour would only be offered again.
 */
export async function markTourDone(tour: TourId): Promise<TourRecords> {
  const current = await fetchTourRecords();
  if (current.tours[tour]) return current.tours;
  const tours: TourRecords = { ...current.tours, [tour]: true };
  if (current.exists) await apiClient.patch(PREFERENCE_URL, { data: tours });
  else await apiClient.post('/api/preference/', { key: PREFERENCE_KEY, data: tours });
  return tours;
}

/**
 * Tell the backend how a tour ended.
 * The preference above only says the tour is done; this keeps whether it was
 * finished or skipped, and where.
 */
export async function recordTourOutcome(
  tour: TourId,
  outcome: 'completed' | 'skipped',
  step?: string
): Promise<void> {
  await sendBackendEvent(`tour_${outcome}`, { tour, ...(step ? { step } : {}) });
}
